import { OutputConfig } from '../types/common';
import { formatCurrency } from '@/services/string';
import { FieldConfig, SectionConfig } from '../types/common';
import z from 'zod';

function calculateDownPayment(data: CashToCloseInput): number {
  return Math.max(0, data.purchasePrice - data.loanAmount);
}

function calculateReservesHoldbacks(data: CashToCloseInput): number {
  return (data.constructionHoldback || 0) + (data.interestReserve || 0);
}

function calculateLenderFees(data: CashToCloseInput): number {
  return (
    (data.originationPoints || 0) +
    (data.processingFees || 0) +
    (data.underwritingFees || 0) +
    (data.documentPreparationFees || 0) +
    (data.lenderMiscellaneous || 0)
  );
}

function calculateThirdPartyFees(data: CashToCloseInput): number {
  return (
    (data.titleInsurance || 0) +
    (data.appraisal || 0) +
    (data.brokerFees || 0) +
    (data.taxes || 0) +
    (data.survey || 0) +
    (data.thirdPartyMiscellaneous || 0)
  );
}

function calculateCashToClose(data: CashToCloseInput): number {
  return (
    calculateDownPayment(data) +
    calculateReservesHoldbacks(data) +
    calculateLenderFees(data) +
    calculateThirdPartyFees(data)
  );
}

const CashToCloseInputBaseSchema = z.object({
  purchasePrice: z
    .number({ message: 'Purchase price is required' })
    .min(0, { message: 'Purchase price must be at least $0' }),
  loanAmount: z
    .number({ message: 'Loan amount is required' })
    .min(0, { message: 'Loan amount must be at least $0' }),
  constructionHoldback: z
    .number({ message: 'Construction holdback is required' })
    .min(0, { message: 'Construction holdback must be at least $0' }),
  interestReserve: z
    .number({ message: 'Interest reserve is required' })
    .min(0, { message: 'Interest reserve must be at least $0' }),
  originationPoints: z
    .number({ message: 'Origination points are required' })
    .min(0, { message: 'Origination points must be at least $0' }),
  processingFees: z
    .number({ message: 'Processing fees are required' })
    .min(0, { message: 'Processing fees must be at least $0' }),
  underwritingFees: z
    .number({ message: 'Underwriting fees are required' })
    .min(0, { message: 'Underwriting fees must be at least $0' }),
  documentPreparationFees: z
    .number({ message: 'Document preparation fees are required' })
    .min(0, { message: 'Document preparation fees must be at least $0' }),
  lenderMiscellaneous: z
    .number({ message: 'Miscellaneous lender fees are required' })
    .min(0, { message: 'Miscellaneous lender fees must be at least $0' }),
  titleInsurance: z
    .number({ message: 'Title insurance is required' })
    .min(0, { message: 'Title insurance must be at least $0' }),
  appraisal: z
    .number({ message: 'Appraisal is required' })
    .min(0, { message: 'Appraisal must be at least $0' }),
  brokerFees: z
    .number({ message: 'Broker fees are required' })
    .min(0, { message: 'Broker fees must be at least $0' }),
  taxes: z
    .number({ message: 'Taxes are required' })
    .min(0, { message: 'Taxes must be at least $0' }),
  survey: z
    .number({ message: 'Survey is required' })
    .min(0, { message: 'Survey must be at least $0' }),
  thirdPartyMiscellaneous: z
    .number({ message: 'Miscellaneous third party fees are required' })
    .min(0, { message: 'Miscellaneous third party fees must be at least $0' })
});

export type CashToCloseInput = z.infer<typeof CashToCloseInputBaseSchema>;

export const CashToCloseKeys = CashToCloseInputBaseSchema.keyof().options;

export const CashToCloseInputSchema = CashToCloseInputBaseSchema;

export const CashToCloseSectionConfig: SectionConfig<CashToCloseInput> = [
  {
    title: 'Loan details',
    fields: [
      'purchasePrice',
      'loanAmount',
      'constructionHoldback',
      'interestReserve'
    ]
  },
  {
    title: 'Lender fees',
    fields: [
      'originationPoints',
      'processingFees',
      'underwritingFees',
      'documentPreparationFees',
      'lenderMiscellaneous'
    ]
  },
  {
    title: 'Third party fees',
    fields: [
      'titleInsurance',
      'appraisal',
      'brokerFees',
      'taxes',
      'survey',
      'thirdPartyMiscellaneous'
    ]
  }
];

export const getCashToCloseFieldConfig = (
  _data: CashToCloseInput
): FieldConfig<CashToCloseInput> => ({
  purchasePrice: {
    name: 'purchasePrice',
    type: 'currency',
    label: 'Purchase price',
    placeholder: '200,000.00'
  },
  loanAmount: {
    name: 'loanAmount',
    type: 'currency',
    label: 'Loan amount',
    placeholder: '200,000.00'
  },
  constructionHoldback: {
    name: 'constructionHoldback',
    type: 'currency',
    label: 'Construction holdback',
    placeholder: '50,000.00'
  },
  interestReserve: {
    name: 'interestReserve',
    type: 'currency',
    label: 'Interest reserve',
    placeholder: '100.00'
  },
  originationPoints: {
    name: 'originationPoints',
    type: 'currency',
    label: 'Origination points',
    placeholder: '5,000.00'
  },
  processingFees: {
    name: 'processingFees',
    type: 'currency',
    label: 'Processing fees',
    placeholder: '5,000.00'
  },
  underwritingFees: {
    name: 'underwritingFees',
    type: 'currency',
    label: 'Underwriting fees',
    placeholder: '5,000.00'
  },
  documentPreparationFees: {
    name: 'documentPreparationFees',
    type: 'currency',
    label: 'Document preparation fees',
    placeholder: '5,000.00'
  },
  lenderMiscellaneous: {
    name: 'lenderMiscellaneous',
    type: 'currency',
    label: 'Miscellaneous',
    placeholder: '5,000.00'
  },
  titleInsurance: {
    name: 'titleInsurance',
    type: 'currency',
    label: 'Title insurance',
    placeholder: '3,000.00'
  },
  appraisal: {
    name: 'appraisal',
    type: 'currency',
    label: 'Appraisal',
    placeholder: '500.00'
  },
  brokerFees: {
    name: 'brokerFees',
    type: 'currency',
    label: 'Broker Fees',
    placeholder: '500.00'
  },
  taxes: {
    name: 'taxes',
    type: 'currency',
    label: 'Taxes',
    placeholder: '500.00'
  },
  survey: {
    name: 'survey',
    type: 'currency',
    label: 'Survey',
    placeholder: '500.00'
  },
  thirdPartyMiscellaneous: {
    name: 'thirdPartyMiscellaneous',
    type: 'currency',
    label: 'Miscellaneous',
    placeholder: '500.00'
  }
});

export const getCashToCloseOutputConfig = (
  data: CashToCloseInput
): OutputConfig => {
  const downPayment = calculateDownPayment(data);
  const reservesHoldbacks = calculateReservesHoldbacks(data);
  const lenderFees = calculateLenderFees(data);
  const thirdPartyFees = calculateThirdPartyFees(data);
  const cashToClose = calculateCashToClose(data);
  return [
    {
      fields: [
        {
          label: 'Down payment',
          value: formatCurrency(downPayment)
        },
        {
          label: 'Reserves & holdbacks',
          value: formatCurrency(reservesHoldbacks)
        },
        {
          label: 'Total fees',
          value: formatCurrency(lenderFees + thirdPartyFees)
        },
        {
          label: 'Cash To Close',
          value: formatCurrency(cashToClose)
        }
      ]
    }
  ];
};
